import classNames from 'classnames'
import { AnimatePresence, motion } from 'framer-motion'
import { FC, memo, ReactNode } from 'react'
import ReactDOM from 'react-dom'
import Close from '~icons/material-symbols/close'

// ヘッダーなしの場合のpropsサブタイプ
type WithoutHeaderProps = {
  /**
   * ダイアログ上部の、タイトルと閉じるボタンが表示されるヘッダー領域を省略するかどうか。
   *
   * trueを指定するとヘッダーが表示されず、ダイアログ全域をコンテンツとして扱える。このとき閉じるボタンはコンテンツ領域の右上に表示される。
   *
   * falseまたは未指定の場合、ヘッダーが表示される。ヘッダーにはタイトルと閉じるボタンが表示される。
   */
  headerLess: true
  /**
   * ダイアログ上部のヘッダーに表示するタイトル文字列。ヘッダーを省略する場合は指定出来ない。
   */
  title?: undefined
}

// ヘッダーありの場合のpropsサブタイプ
type WithHeaderProps = {
  /**
   * ダイアログ上部の、タイトルと閉じるボタンが表示されるヘッダー領域を省略するかどうか。
   *
   * trueを指定するとヘッダーが表示されず、ダイアログ全域をコンテンツとして扱える。このとき閉じるボタンはコンテンツ領域の右上に表示される。
   *
   * falseまたは未指定の場合、ヘッダーが表示される。ヘッダーにはタイトルと閉じるボタンが表示される。
   */
  headerLess?: false
  /**
   * ダイアログ上部のヘッダーに表示するタイトル文字列。この文字列がヘッダーに表示される。省略すると空欄になる。
   */
  title?: string
}

type Props = (WithoutHeaderProps | WithHeaderProps) & {
  id?: string
  isOpen: boolean
  // TODO: onCloseという命名だが、ダイアログが閉じられるときに実行されるわけではなく、×ボタンとダイアログ外クリック時に実行されるのみ。以下タスクで命名について議論予定
  // https://break-tmc.atlassian.net/browse/CREW-6549
  onClose: () => void
  /**
   * trueの場合、ユーザー操作（背景クリック・閉じるボタン）でダイアログを閉じられないようにする。
   */
  closeDisabled?: boolean
  children?: ReactNode
  className?: string
}

export const Modal: FC<Props> = memo((props) =>
  ReactDOM.createPortal(
    <AnimatePresence>
      {props.isOpen && (
        <motion.div
          key="modal"
          initial={{ opacity: 0 }}
          animate={{
            opacity: 1,
            transition: {
              duration: 0.2,
            },
          }}
          exit={{
            opacity: 0,
            transition: {
              duration: 0.2,
            },
          }}
          // ダイアログが他要素よりも前面に出るよう「relative z-60」を指定、左のメニューのz-indexが50のため
          className="relative z-60"
        >
          <div className="fixed inset-0 mx-auto min-h-[100dvh] flex flex-col justify-center overflow-hidden">
            <div
              className="fixed w-full h-full bg-black/50"
              onClick={props.closeDisabled ? undefined : props.onClose}
            />
            <div
              id={props.id ?? 'modal'}
              className={classNames(
                'relative max-h-[100dvh] crew-bg-default  shadow-xl ring-1 ring-gray-900/5 p-5 h-full w-full rounded-none sm:h-auto lg:mx-auto lg:w-auto lg:max-w-4xl xl:max-w-5xl sm:rounded-lg',
                props.className
              )}
            >
              <div className="flex flex-col h-full gap-3">
                {/* ヘッダーを表示する場合 */}
                {props.headerLess !== true && (
                  <div className="flex flex-row items-center">
                    {/* タイトル */}
                    <div className="grow">
                      {props.title && (
                        <span className="text-lg">{props.title}</span>
                      )}
                    </div>
                    {/* 閉じるボタン */}
                    {!props.closeDisabled && (
                      <div onClick={props.onClose}>
                        <Close
                          width={24}
                          height={24}
                          className="cursor-pointer"
                        />
                      </div>
                    )}
                  </div>
                )}
                <div className="grow min-h-0 flex flex-col justify-center">
                  {/* ヘッダーがない場合、コンテンツ領域の右上に閉じるボタンを表示 */}
                  {props.headerLess === true && !props.closeDisabled && (
                    <div
                      onClick={props.onClose}
                      className="absolute top-5 right-5"
                    >
                      <Close
                        width={24}
                        height={24}
                        className="cursor-pointer"
                      />
                    </div>
                  )}
                  {props.children}
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  )
)
